import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { VIEW_ENTITY_TYPES } from '../../utils/DataConstants';
import { isWorkOrderLabelsTrackEnabled } from '../../data/workOrderStarLabels';
import WorkOrdersViewsPopover from './WorkOrdersViewsPopover';
import TrackedViewTabIcon from '../StarLabels/TrackedViewTabIcon';

const renderTabName = (view: ViewOption): React.ReactNode => {
	if (view.url === 'starred') {
		// Star/bell, or a ringing red bell once any of the user's reminders are due. Its colors are
		// set with !important so they survive the active tab's dark text color (SubnavTabs `.active`),
		// and it sits in a 22px box (the text tabs' line box: 14px × antd's 1.5715 line-height) so
		// the Tracked <li> matches its siblings' height.
		return <TrackedViewTabIcon />;
	}
	return view.name;
};

export const WORK_ORDERS_OVERVIEW_TABS_COLLECTION = 'workOrdersSavedFilterPinnedViews';

export interface ViewOption {
	id: string;
	name: string;
	url?: string;
	filterQuery?: string;
	filterValue?: any;
	isPublic?: boolean;
	isPinned?: boolean;
	createdAt?: string;
	createdBy?: string;
	entityType?: string;
	isDeleted?: boolean;
	updatedAt?: string;
}

export interface TabView {
	key: string;
	name: string;
	viewOption: ViewOption;
}

interface UseViewTabsProps {
	defaultViewOption: ViewOption | null;
	savedViewOptions: ViewOption[];
	baseViewOptions: ViewOption[];
	savedViewId?: string;
	baseRouteKey?: string;
	onViewSelect: (viewOption: ViewOption) => void;
}

interface UseViewTabsReturn {
	tabs: Array<{ key: string; name: React.ReactNode }>;
	handleTabChange: (key: string) => void;
	checkTabActive: (key: string) => boolean;
}

export const useViewTabs = ({
	defaultViewOption,
	savedViewOptions,
	baseViewOptions,
	savedViewId,
	baseRouteKey,
	onViewSelect,
}: UseViewTabsProps): UseViewTabsReturn => {
	const tabViews = useMemo(() => {
		const ordered = [];

		baseViewOptions.forEach((view) => {
			ordered.push(view);
		});

		savedViewOptions
			.filter(
				(view) => view.isPinned || nullSafeGet('id', view) === nullSafeGet('id', defaultViewOption)
			)
			.forEach((view) => {
				ordered.push(view);
			});

		return ordered;
	}, [savedViewOptions, baseViewOptions, defaultViewOption]);

	const tabs = useMemo(
		() =>
			tabViews.map((tab) => ({
				key: tab.id,
				name: renderTabName(tab),
			})),
		[tabViews]
	);

	const handleTabChange = useCallback(
		(key: string) => {
			const viewOption = tabViews.find((view) => `${view.id}` === `${key}`);
			if (!viewOption) {
				return;
			}
			onViewSelect(viewOption);
		},
		[onViewSelect, tabViews]
	);

	const checkTabActive = useCallback(
		(key: string) => {
			const matchingBaseView = baseViewOptions.find((view) => `${view.id}` === `${key}`);
			if (matchingBaseView) {
				const routeSegment = matchingBaseView.url || `${matchingBaseView.id}`;
				return !savedViewId && baseRouteKey === routeSegment;
			}
			return !!savedViewId && `${key}` === `${savedViewId}`;
		},
		[baseRouteKey, savedViewId, baseViewOptions]
	);

	return {
		tabs,
		handleTabChange,
		checkTabActive,
	};
};

interface UseWorkOrdersViewTabsProps {
	basePath: string;
	baseViewOptions: ViewOption[];
	defaultView: any;
	workOrderViews: any[];
	workOrderViewsLoading: boolean;
	attentionDashboards: any;
	saveAttentionDashboard: (entity: any) => Promise<any>;
	fetchWorkOrderViews: (
		params: any,
		targetCollectionName: string,
		pagination: any,
		sorting: any,
		filters: any,
		addToTargetCollection?: any
	) => Promise<any>;
	getWorkOrderDefaultView: () => Promise<any>;
}

export const useWorkOrdersViewTabs = ({
	basePath,
	baseViewOptions: rawBaseViewOptions,
	defaultView,
	workOrderViews,
	workOrderViewsLoading,
	attentionDashboards,
	saveAttentionDashboard,
	fetchWorkOrderViews,
	getWorkOrderDefaultView,
}: UseWorkOrdersViewTabsProps) => {
	const [loading, setLoading] = useState(!defaultView);
	const history = useHistory();
	const location = useLocation();

	// Hide the "Tracked" (starred) base-view tab when the labels/stars feature flag is off.
	const baseViewOptions = useMemo(
		() =>
			isWorkOrderLabelsTrackEnabled()
				? rawBaseViewOptions
				: (rawBaseViewOptions || []).filter((view) => view.url !== 'starred'),
		[rawBaseViewOptions]
	);

	const pathSegments = useMemo(
		() => location.pathname.split('/').filter(Boolean),
		[location.pathname]
	);

	const savedViewId = useMemo(() => {
		if (pathSegments.length < 2) return undefined;
		const last = pathSegments[pathSegments.length - 1];
		const secondLast = pathSegments[pathSegments.length - 2];
		return secondLast === 'view' ? last : undefined;
	}, [pathSegments]);

	const baseRouteKey = useMemo(() => {
		const last = pathSegments[pathSegments.length - 1];
		return baseViewOptions.some((view) => (view.url || `${view.id}`) === last) ? last : undefined;
	}, [pathSegments, baseViewOptions]);

	const getFilterQueryString = useCallback((viewOption: any) => {
		const filterQuery = (nullSafeGet('filterQuery', viewOption) || '').replace(/^\?/, '');
		if (!filterQuery) return '';
		return filterQuery
			.split('&')
			.filter((segment: string) => segment && !segment.startsWith('viewId='))
			.join('&');
	}, []);

	const applyViewSelection = useCallback(
		(viewOption: any) => {
			if (!viewOption) return;
			const isBaseView = baseViewOptions.some((view) => view.id === viewOption.id);
			if (isBaseView) {
				history.push(`${basePath}/${viewOption.url || viewOption.id}`);
				return;
			}
			history.push(`${basePath}/view/${viewOption.id}`);
		},
		[basePath, baseViewOptions, history]
	);

	const { tabs, handleTabChange, checkTabActive } = useViewTabs({
		defaultViewOption: defaultView,
		savedViewOptions: workOrderViews,
		baseViewOptions,
		savedViewId,
		baseRouteKey,
		onViewSelect: applyViewSelection,
	});

	const tabsRightActions = useCallback(
		() => (
			<WorkOrdersViewsPopover
				allViewOptions={workOrderViews}
				selectedViewId={savedViewId}
				workOrderViewsLoading={workOrderViewsLoading}
				attentionDashboards={attentionDashboards}
				saveAttentionDashboard={saveAttentionDashboard}
				onViewSelect={applyViewSelection}
				defaultViewId={nullSafeGet('id', defaultView)}
				defaultView={defaultView}
				fetchWorkOrderViews={fetchWorkOrderViews}
			/>
		),
		[
			workOrderViews,
			savedViewId,
			workOrderViewsLoading,
			attentionDashboards,
			saveAttentionDashboard,
			applyViewSelection,
			defaultView,
			fetchWorkOrderViews,
		]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		const fetchTask = getWorkOrderDefaultView();
		if (!defaultView) {
			fetchTask.finally(() => setLoading(false));
		}
		fetchWorkOrderViews(
			null,
			WORK_ORDERS_OVERVIEW_TABS_COLLECTION,
			{ current: 1, pageSize: 100 },
			{ sort_by: 'order', order: 'ascend' },
			{ entityType: VIEW_ENTITY_TYPES.WORKORDERS, isPinned: true }
		);
	}, []);

	const redirectTarget = useMemo(() => {
		if (defaultView && nullSafeGet('id', defaultView)) {
			const defaultId = nullSafeGet('id', defaultView);
			const isBaseView = baseViewOptions.some((view) => view.id === defaultId);
			if (isBaseView) {
				return `${basePath}/${nullSafeGetOrElse('url', defaultView, defaultId)}`;
			}
			// A base view that's been gated out by the feature flag (e.g. the starred "Tracked" view
			// when workorder-labels-track is off) is NOT a saved view — routing to /view/<id> would
			// land on a non-existent saved view. Only treat the id as a saved view if it isn't one of
			// the (unfiltered) base views; otherwise fall through to the default tab.
			const isGatedBaseView = (rawBaseViewOptions || []).some((view) => view.id === defaultId);
			if (defaultId && !isGatedBaseView) {
				return `${basePath}/view/${defaultId}`;
			}
		}
		return `${basePath}/active`;
	}, [basePath, baseViewOptions, rawBaseViewOptions, defaultView]);

	return {
		loading,
		savedViewId,
		redirectTarget,
		tabs,
		handleTabChange,
		checkTabActive,
		tabsRightActions,
		getFilterQueryString,
	};
};
